<template>
  <div class="LLConfirmModal" :class="classList">
    <div class="LLConfirmModal__background" @click="onBackplateClick"></div>
    <div class="LLConfirmModal__block" :style="confirmModalStyle">
      <!-- Full-width area above the header (e.g. a progress bar). The consumer is
           responsible for its own padding, so an empty slot adds no space. -->
      <div v-if="slotsVisibility.top" class="LLConfirmModal__top">
        <slot name="top"></slot>
      </div>
      <header v-if="slotsVisibility.title" class="LLConfirmModal__header">
        <h3 class="LLConfirmModal__title">
          <slot name="title"></slot>
        </h3>
        <LLButton
          v-if="withCloseButton"
          class="LLConfirmModal__close-button"
          primary-transparent
          tiny
          no-x-padding
          @click="close"
        >
          <CloseRegularIcon />
        </LLButton>
      </header>
      <div class="LLConfirmModal__content" :style="contentStyle">
        <slot name="default"></slot>
      </div>
      <div v-if="slotsVisibility.actions" class="LLConfirmModal__actions">
        <slot name="actions"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import CloseRegularIcon from '@/assets/icons/CloseRegularIcon.vue'
import LLButton from '@/components/common/ui-components/LLButton.vue'

export default {
  name: 'LLConfirmModal',
  components: { LLButton, CloseRegularIcon },
  props: {
    opened: { type: Boolean, default: false },
    maxWidth: { type: Number, default: null },
    minWidth: { type: Number, default: null },
    width: { type: [Number, String], default: null },
    maxContentHeight: { type: String, default: null },
    contentScrollable: { type: Boolean, default: false },
    closeOnBackdrop: { type: Boolean, default: false },
    success: { type: Boolean, default: false },
    fixedHeader: { type: Boolean, default: false },
    fixedWidth: { type: Boolean, default: false },
    withCloseButton: { type: Boolean, default: false }
  },
  data() {
    return {
      animationOpened: false,
      stateOpened: false,
      closing: false,
      slotsVisibility: {
        top: false,
        title: false,
        actions: false
      }
    }
  },
  computed: {
    classList() {
      return {
        [`${this.$options.name}_content-scrollable`]: this.contentScrollable,
        [`${this.$options.name}_success`]: this.success,
        [`${this.$options.name}_fixed-header`]: this.fixedHeader,
        [`${this.$options.name}_fixed-width`]: this.fixedWidth
      }
    },
    confirmModalStyle() {
      return {
        width: this.width ? `${this.width}` : undefined,
        maxWidth: this.maxWidth ? `${this.maxWidth}px` : undefined,
        minWidth: this.minWidth ? `${this.minWidth}px` : undefined
      }
    },
    contentStyle() {
      return {
        maxHeight: this.maxContentHeight,
        overflow: this.contentScrollable ? 'auto' : 'hidden'
      }
    }
  },
  watch: {
    opened: {
      immediate: true,
      handler(to) {
        if (to) {
          this.open()
        } else {
          this.close()
        }
      }
    },
    stateOpened: {
      handler(to) {
        if (!to) {
          this.$emit('update:opened', false)
        }
      }
    }
  },
  created() {
    this.checkSlotsVisibility()
  },
  beforeUpdate() {
    this.checkSlotsVisibility()
  },
  mounted() {
    document.body.classList.add('block-scroll')
  },
  destroyed() {
    document.body.classList.remove('block-scroll')
  },
  methods: {
    onBackplateClick() {
      if (this.closeOnBackdrop) {
        this.close()
      }
    },
    checkSlotsVisibility() {
      this.$nextTick(() => {
        this.slotsVisibility.top = !!this.$slots?.top?.[0]
        this.slotsVisibility.title = !!this.$slots?.title?.[0]
        this.slotsVisibility.actions = !!this.$slots?.actions?.[0]
      })
    },
    open() {
      this.stateOpened = true
      this.$nextTick(() => {
        this.animationOpened = true
      })
    },
    close() {
      if (this.closing) {
        return
      }
      this.animationOpened = false
      setTimeout(() => {
        this.stateOpened = false
      }, 310)
    }
  }
}
</script>

<style lang="scss" scoped>
.LLConfirmModal {
  $root: &;
  z-index: 10000;
  @apply w-full h-full fixed left-0 top-0 p-8 flex items-center justify-center overflow-auto;

  &__background {
    @apply w-full h-full fixed bg-neutral-01-800 opacity-20;
  }
  &__block {
    @apply bg-white shadow-dropdown m-auto z-50 relative;
    @apply w-full;
    @screen md {
      @apply w-auto;
    }
    // Keeps the dialog width constant (fills up to `max-width` instead of shrinking to its
    // content) — for multi-step modals that would otherwise resize as the content changes.
    #{$root}_fixed-width & {
      @screen md {
        @apply w-full;
      }
    }
    //#{$root}_no-padding & {
    //  @apply p-0;
    //}
  }
  &__header {
    @apply flex items-center justify-between;
    @apply px-6 pt-6;
    &:last-child {
      @apply mb-0;
    }
    #{$root}_fixed-header & {
      @apply p-6 sticky bg-white z-50 border-b border-neutral-01-50 shadow;
      top: -2rem;
    }
  }
  &__close-button {
  }
  &__title {
    @apply truncate;
    #{$root}_success & {
      @apply text-success-01;
    }
  }
  &__actions {
    @apply mt-4 flex flex-wrap justify-end pb-6 px-6;
    ::v-deep > * {
      @apply ml-4;
      &:first-child {
        @apply ml-0;
      }
    }
  }
  &__content {
    #{$root}_content-scrollable & {
      width: calc(100% + 24px);
    }
    &:last-child {
      @apply pb-6;
    }
    @apply px-6 mt-4;
    &:first-child {
      @apply mt-0 pt-6;
    }
  }
}
</style>
